import axios from 'axios'
import React, { useEffect, useMemo, useState } from 'react'

import type { UserDetailsResponse } from '@/utils/api/user-service/models'

import { clearAuthToken, getAuthToken } from '@/lib/session'
import { getCurentUserDetails } from '@/utils/api/user-service'

import type { TAuthContext } from './auth-context'

import { AuthContext } from './auth-context'

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserDetailsResponse | null>(null)
  const { token, type } = getAuthToken()
  const [isLoading, setIsLoading] = useState(() => !!token && !!type)

  useEffect(() => {
    let cancelled = false
    async function hydrate() {
      try {
        if (!token || !type) {
          setUser(null)
          return
        }
        const authHeader = `${type} ${token}`
        axios.defaults.headers.common.Authorization = authHeader
        if (cancelled)
          return
        const me = await getCurentUserDetails()
        setUser(me.data)
      }
      catch {
        // ignore
      }
      finally {
        if (!cancelled)
          setIsLoading(false)
      }
    }
    hydrate()
    return () => {
      cancelled = true
    }
  }, [token, type])

  const value = useMemo<TAuthContext>(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      login: setUser,
      logout: () => {
        clearAuthToken()
        setUser(null)
      },
    }),
    [user, isLoading],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthProvider
